import type { Debt, IncomeSource, MonthlyLedgerEntry, RecurringExpense } from '@/types'

/**
 * Shape validators for localStorage payloads.
 *
 * Every LocalStorageRepo `read()` runs the raw JSON-parsed value through
 * one of these before returning. Without this layer, a `JSON.parse` that
 * succeeds but produces the wrong runtime shape (e.g. an object where an
 * array is expected, after a malformed restore or hand-edit) lets the
 * `as Debt[]` cast lie to downstream consumers — and `.filter()` /
 * `.reduce()` calls crash a tick later.
 *
 * The bar is deliberately low: we check **only** that the top level is
 * the right collection type and that each item has the minimum identity
 * field (`id`) so downstream lookups don't blow up. We don't verify
 * every property of every entity because:
 *
 *   1. Pre-v0.8.x backups predate some fields — strict shape checks
 *      would falsely reject legitimately old user data.
 *   2. The engine + UI are already defensive against missing optional
 *      fields (the entity types have many optional fields by design).
 *   3. False rejections silently wipe data; false acceptances surface
 *      visible bugs that can be reported and fixed. Prefer the latter.
 *
 * When a payload fails validation, repos call `console.warn` and fall
 * back to their seeded default (empty array for the four collection
 * repos; null for the recovery-anchor singleton). The error boundary
 * still catches anything that slips past.
 */

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
}

function hasStringId(v: unknown): v is { id: string } {
  return isRecord(v) && typeof v.id === 'string' && v.id.length > 0
}

/** True if `v` is `Debt[]`-shaped enough to not crash downstream `.filter()`. */
export function isDebtsArray(v: unknown): v is Debt[] {
  return Array.isArray(v) && v.every(hasStringId)
}

/** True if `v` is `IncomeSource[]`-shaped. Same minimum bar as debts. */
export function isIncomeSourcesArray(v: unknown): v is IncomeSource[] {
  return Array.isArray(v) && v.every(hasStringId)
}

/** True if `v` is `MonthlyLedgerEntry[]`-shaped. Adds a `month` check
 *  because the ledger store keys its month-navigation on this field
 *  and a missing/malformed `month` would break activeMonth resolution. */
export function isLedgerEntriesArray(v: unknown): v is MonthlyLedgerEntry[] {
  return (
    Array.isArray(v) &&
    v.every((item) => {
      if (!hasStringId(item)) return false
      const month = (item as Record<string, unknown>).month
      return typeof month === 'string' && /^[0-9]{4}-[0-9]{2}$/.test(month)
    })
  )
}

/** True if `v` is `RecurringExpense[]`-shaped. */
export function isRecurringExpensesArray(v: unknown): v is RecurringExpense[] {
  return Array.isArray(v) && v.every(hasStringId)
}
